// src/pages/Sifter.js — the main page orchestrator (four UI states)
//
// 1. Scanning  — live progress counts
// 2. Report    — headline numbers, per-collection accounting, orphan grid,
//                missing list, backup export, gated deletion
// 3. Cleaning  — per-CID live log, pause/resume, verified outcomes
// 4. (Logged out is handled by ProtectedRoute → Login)

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { runScan } from '../lib/scan';
import { TEMP_NSID, sweepTempCollection, cleanupOneBlob } from '../lib/cleanup';
import { RateLimitTracker } from '../lib/rateLimit';
import ScanProgress from '../components/ScanProgress';
import ScanReport from '../components/ScanReport';
import CleanupLog from '../components/CleanupLog';
import './Sifter.css';

const RUN_STATE_KEY = (did) => `sifter:run:${did}`;
const PACING_MS = 1000; // ~1s between orphans (§4); headers authoritative at runtime

const Sifter = () => {
  const { agent, session, logout } = useAuth();
  const [phase, setPhase] = useState('idle'); // idle | scanning | report | cleaning
  const [scanResult, setScanResult] = useState(null);
  const [progress, setProgress] = useState({});
  const [scanError, setScanError] = useState(null);
  const [cleanupState, setCleanupState] = useState(null);
  const [strays, setStrays] = useState(null);

  const did = session?.did;
  const runIdRef = useRef(0);
  const cleanupControlRef = useRef({ paused: false, stopped: false });

  const pdsUrl = useMemo(() => {
    if (!session) return null;

    return session.pdsUrl || session.iss || null;
  }, [session]);

  // On first load: sweep our own temp collection (hygiene, §4).
  useEffect(() => {
    if (!agent || !did) return;
    let cancelled = false;
    (async () => {
      try {
        const deleted = await sweepTempCollection({
          did,
          listRecords: (params) => agent.api.com.atproto.repo.listRecords(params),
          deleteRecord: (params) => agent.api.com.atproto.repo.deleteRecord(params),
        });

        if (!cancelled && deleted > 0) setStrays(deleted);
      } catch (err) {
        console.warn('Temp collection sweep failed (non-fatal):', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agent, did]);

  // Resume check: is there a saved cleanup run for this DID?
  useEffect(() => {
    if (!did) return;

    try {
      const raw = localStorage.getItem(RUN_STATE_KEY(did));

      if (raw) {
        const saved = JSON.parse(raw);

        const pending = saved?.orphans?.filter(
          (o) => o.status === 'pending' || o.status === 'in-progress'
        );

        if (pending?.length > 0) {
          setCleanupState(saved);
        } else {
          localStorage.removeItem(RUN_STATE_KEY(did));
        }
      }
    } catch (err) {
      console.warn('Failed to read saved run state:', err);
    }
  }, [did]);

  const runScanNow = useCallback(async () => {
    if (!agent || !did) return;
    const runId = ++runIdRef.current;
    setPhase('scanning');
    setScanError(null);
    setScanResult(null);
    setProgress({});

    try {
      const result = await runScan({
        did,
        listBlobs: (params) => agent.api.com.atproto.sync.listBlobs(params),
        describeRepo: (params) => agent.api.com.atproto.repo.describeRepo(params),
        listRecords: (params) => agent.api.com.atproto.repo.listRecords(params),
        listMissingBlobs: (params) => agent.api.com.atproto.repo.listMissingBlobs(params),
        onProgress: {
          onHeld: (n) => setProgress((p) => ({ ...p, held: n })),
          onCollection: (nsid, records, blobRefs) =>
            setProgress((p) => ({
              ...p,
              collections: (p.collections || []).concat([{ nsid, records, blobRefs }]),
            })),
          onRecord: (n) => setProgress((p) => ({ ...p, records: n })),
        },
      });

      if (runId !== runIdRef.current) return; // stale run
      setScanResult(result);
      setPhase('report');
    } catch (err) {
      if (runId !== runIdRef.current) return;
      console.error('Scan failed:', err);
      setScanError(err?.message || String(err));
      setPhase('report');
    }
  }, [agent, did]);

  /**
   * Run (or resume) the cleanup loop over the orphan list. Serial, paced,
   * pausable, resumable, idempotent per CID. State persists to localStorage
   * keyed by DID after every status change.
   */
  const runCleanupLoop = useCallback(
    async (state) => {
      if (!agent || !did) return;
      const control = cleanupControlRef.current;
      control.paused = false;
      control.stopped = false;
      const rateLimiter = new RateLimitTracker({});
      const ref = { current: { ...state, orphans: state.orphans.map((o) => ({ ...o })) } };

      const save = () => {
        try {
          localStorage.setItem(RUN_STATE_KEY(did), JSON.stringify(ref.current));
        } catch (err) {
          console.warn('Failed to persist run state:', err);
        }
      };

      const setState = (fn) => {
        setCleanupState((prev) => {
          const next = fn(prev);
          ref.current = next;

          return next;
        });
      };

      const onStatus = (cid, status, detail) => {
        setState((prev) => ({
          ...prev,
          orphans: (prev?.orphans || []).map((o) =>
            o.cid === cid ? { ...o, status, detail, updatedAt: new Date().toISOString() } : o
          ),
        }));
        save();
      };

      const onRunNote = (note) => {
        setState((prev) => ({ ...prev, note, updatedAt: new Date().toISOString() }));
        save();
      };

      setPhase('cleaning');

      // Sweep strays right before starting (idempotent).
      try {
        const deleted = await sweepTempCollection({
          did,
          listRecords: (params) => agent.api.com.atproto.repo.listRecords(params),
          deleteRecord: (params) => agent.api.com.atproto.repo.deleteRecord(params),
        });

        if (deleted > 0) onRunNote(`Swept ${deleted} stray temp record(s) before starting`);
      } catch (err) {
        onRunNote(`Temp sweep failed (continuing): ${err?.message || err}`);
      }

      for (const o of ref.current.orphans) {
        if (control.stopped) break;

        // Pause support
        while (control.paused && !control.stopped) {
          await new Promise((r) => setTimeout(r, 300));
        }

        if (control.stopped) break;

        if (o.status === 'deleted' || o.status === 'undeletable' || o.status === 'already-absent') {
          continue;
        }

        onStatus(o.cid, 'in-progress');

        try {
          const result = await cleanupOneBlob({
            blob: { did, cid: o.cid },
            meta: { mimeType: 'application/octet-stream', size: 0 },
            createRecord: (params) => agent.api.com.atproto.repo.createRecord(params),
            deleteRecord: (params) => agent.api.com.atproto.repo.deleteRecord(params),
            listBlobs: (params) => agent.api.com.atproto.sync.listBlobs(params),
            fetchBlobHeaders: async ({ did: d, cid }) => {
              // getBlob throws on 404; headers give us Content-Type/Length
              const res = await agent.api.com.atproto.sync.getBlob({ did: d, cid });
              const headers = res?.headers || {};
              const get = (n) => (headers.get !== undefined ? headers.get(n) : headers[n]);

              return {
                mimeType: get('content-type') || 'application/octet-stream',
                size: Number.parseInt(get('content-length') || '0', 10) || 0,
              };
            },
          });

          onStatus(o.cid, result.outcome, result.detail);

          if (result.outcome === 'still-present') {
            onRunNote('Cleanup aborted: mechanism did not work on this PDS.');
            break;
          }
        } catch (err) {
          onStatus(o.cid, 'undeletable', `Unexpected error: ${err?.message || err}`);
        }

        // Pacing between writes (~1s); observe headers when available
        rateLimiter.observe({});
        await new Promise((r) => setTimeout(r, PACING_MS));
      }

      if (!control.stopped) {
        setState((prev) => ({ ...prev, completedAt: new Date().toISOString() }));
        save();
      } else {
        onRunNote('Paused by user — state saved, resume anytime.');
      }
    },
    [agent, did]
  );

  const startCleanup = useCallback(
    async (orphans) => {
      if (!orphans?.length) return;
      await runCleanupLoop({ did, orphans: orphans.map((cid) => ({ cid, status: 'pending' })) });
    },
    [did, runCleanupLoop]
  );

  const resumeCleanup = useCallback(() => {
    if (!cleanupState) return;
    runCleanupLoop(cleanupState);
  }, [cleanupState, runCleanupLoop]);

  const pauseCleanup = useCallback(() => {
    cleanupControlRef.current.paused = true;
  }, []);

  const stopCleanup = useCallback(() => {
    cleanupControlRef.current.stopped = true;
    cleanupControlRef.current.paused = false;
  }, []);

  const clearRunState = useCallback(() => {
    cleanupControlRef.current.stopped = true;

    if (!did) return;

    try {
      localStorage.removeItem(RUN_STATE_KEY(did));
    } catch (err) {
      console.warn(err);
    }

    setCleanupState(null);
    setPhase('report');
  }, [did]);

  return (
    <div className="sifter-page">
      <header className="sifter-header">
        <h1>Sifter</h1>
        <div className="sifter-header-actions">
          {did && <span className="muted did-label">{did}</span>}
          <button onClick={logout} className="logout-button">
            Log out
          </button>
        </div>
      </header>

      {strays !== null && (
        <div className="banner">
          Startup hygiene: swept {strays} leftover temp record(s) from{' '}
          <code>{TEMP_NSID}</code>.
        </div>
      )}

      {scanError && <div className="banner banner-warning">Scan error: {scanError}</div>}

      {phase === 'idle' && (
        <div className="sifter-actions">
          <button onClick={runScanNow} className="scan-button">
            Run scan
          </button>
          {cleanupState && (
            <button onClick={resumeCleanup} className="resume-button">
              Resume cleanup (
              {cleanupState.orphans.filter((o) => o.status === 'pending').length} left)
            </button>
          )}
          <p className="muted">
            Scans your account for blobs your PDS holds that no record references.
            Read-only — nothing is deleted until you back up and explicitly confirm.
          </p>
        </div>
      )}

      {phase === 'scanning' && <ScanProgress progress={progress} />}

      {phase === 'report' && scanResult && (
        <ScanReport
          result={scanResult}
          pdsUrl={pdsUrl}
          did={did}
          agent={agent}
          onStartCleanup={startCleanup}
          onRescan={runScanNow}
          cleanupState={cleanupState}
        />
      )}

      {phase === 'cleaning' && cleanupState && (
        <CleanupLog
          state={cleanupState}
          onPause={pauseCleanup}
          onResume={resumeCleanup}
          onStop={stopCleanup}
          onDone={clearRunState}
        />
      )}
    </div>
  );
};

export default Sifter;
